import React from 'react';
import { 
  Box, 
  Dialog, 
  DialogContent, 
  DialogActions, 
  Typography, 
  Stack, 
  Chip, 
  Button, 
  Zoom 
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export interface IComplianceAlertCardProps {
  open: boolean;
  onClose: () => void;
  menorIdade: boolean;
  pessoaRestrita: boolean;
  nome: string;
}

export const ComplianceAlertCard: React.FC<IComplianceAlertCardProps> = ({ 
  open, 
  onClose, 
  menorIdade, 
  pessoaRestrita,
  nome 
}) => {
  if (!menorIdade && !pessoaRestrita) return null;

  return (
    <Dialog
      open={open}
      TransitionComponent={Zoom}
      transitionDuration={400}
      disableEscapeKeyDown
      onClose={() => { }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(15, 10, 25, 0.45)',
            transition: 'all 0.5s ease',
          }
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 5,
          padding: { xs: 3, sm: 4 },
          maxWidth: '550px',
          boxShadow: '0 25px 50px rgba(238, 147, 56, 0.18), 0 0 0 1px rgba(238, 147, 56, 0.1)',
          background: 'linear-gradient(to bottom, #ffffff, #fffafb)',
          overflow: 'hidden',
          position: 'relative',
        }
      }}
    >
      {/* Decorative top border */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          background: 'linear-gradient(90deg, #ee9338 0%, #ffc46a 100%)',
        }}
      />

      {/* Header section with warning icon */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '24px',
            background: 'linear-gradient(135deg, rgba(238, 147, 56, 0.08) 0%, rgba(255, 196, 106, 0.16) 100%)',
            border: '1px solid',
            borderColor: 'rgba(238, 147, 56, 0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
            color: '#ee9338',
            boxShadow: '0 8px 16px rgba(238, 147, 56, 0.06)',
            animation: 'pulse 2s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)', opacity: 0.9 },
              '50%': { transform: 'scale(1.05)', opacity: 1 },
              '100%': { transform: 'scale(1)', opacity: 0.9 }
            }
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 38 }} />
        </Box>
        <Typography 
          variant="h5" 
          align="center" 
          sx={{ 
            fontWeight: 800, 
            letterSpacing: '-0.5px',
            color: '#c2610d',
          }}
        >
          ALERTA DE CONFORMIDADE
        </Typography>
      </Box>

      {/* Main warning text and chips */}
      <DialogContent sx={{ p: 0, overflow: 'visible' }}>
        <Stack spacing={2.5} alignItems="center">
          <Stack direction="row" spacing={1.5}>
            {menorIdade && (
              <Chip 
                label="Menor de Idade" 
                sx={{ 
                  backgroundColor: 'rgba(238, 147, 56, 0.15)',
                  color: '#c2610d',
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: 'rgba(238, 147, 56, 0.25)'
                }} 
              />
            )}
            {pessoaRestrita && (
              <Chip 
                label="Pessoa Restrita" 
                sx={{ 
                  backgroundColor: 'rgba(238, 147, 56, 0.15)',
                  color: '#c2610d',
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: 'rgba(238, 147, 56, 0.25)'
                }} 
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }} />

      {/* Action actions inside dialog */}
      <DialogActions sx={{ p: 0, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            borderRadius: 3,
            py: 1.25,
            px: 4,
            fontSize: '0.875rem',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: 'none',
            backgroundColor: '#ee9338',
            color: '#ffffff',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              backgroundColor: '#d87d25',
              boxShadow: 'none',
            }
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
